/**
 * handSim - Pure opening-hand simulation engine
 *
 * Shared by SampleHandSimulator (one hand at a time) and BatchSimulation
 * (thousands of hands). Contains no React and no side effects.
 *
 * Model and its deliberate approximations:
 * - The library is main-deck cards only; commanders/companions start outside it.
 * - Lands are assumed to arrive one per turn *from the opening hand only* — draws
 *   are ignored, so `earliestCastTurn` is a lower bound on what a real game offers.
 * - Colored pips are matched to distinct mana sources (exact bipartite matching);
 *   a single source is never asked to pay two pips, even if it taps for two mana.
 * - `{C}` pips and hybrid/phyrexian pips are treated as generic mana.
 * - A card is a set of castable *modes*. Adventures, split cards and modal DFCs
 *   contribute one mode per castable face, so the cheap half counts as an early play.
 *
 * `simulateTurns` adds a solitaire turn model on top: draw, land drop, commander-first
 * greedy cast.
 * It models mana and curve only — no draw spells, tutors, ramp, scry, cost
 * reduction, triggers, or opponents.
 */
import { countManaPips, getCmc, getFullOracleText, getManaCost, getManaProducedCount, rockLandEquivalent } from '@/lib/cardUtils';
import type { CardFace, CardLayout, Color, ImageUris, ScryfallCard } from '@/types/card';
import type { DeckZone, EnrichedDeckCard } from '@/types/deck';

// ============================================================================
// Types
// ============================================================================

export const MANA_COLORS: readonly Color[] = ['W', 'U', 'B', 'R', 'G'];

/** Highest turn `earliestCastTurn` will look ahead to. */
export const MAX_SIM_TURN = 7;

/** One castable face of a card. Normal cards have exactly one. */
export interface SimCastMode {
  /** Face name — differs from the card name for adventures and split cards */
  name: string;
  cmc: number;
  /** Colored pip requirements from this face's mana cost */
  pips: Record<Color, number>;
  /** Generic portion of this face's cost ({N}, {C} and hybrid pips) */
  generic: number;
}

/** Precomputed per-copy simulation record. One entry per physical card in the deck. */
export interface SimCard {
  /** Stable unique id for React keys and identity comparisons */
  key: string;
  /** DeckCard document id (for opening card details) */
  deckCardId: string;
  scryfallId: string;
  name: string;
  imageUris: ImageUris | null;
  isLand: boolean;
  isManaRock: boolean;
  /** Mana value of the front face — mirrors `modes[0].cmc` */
  cmc: number;
  /** Castable faces in printed order. Length 1 for normal cards. */
  modes: SimCastMode[];
  /** Colors this card produces as a mana source (empty for non-sources) */
  producedColors: Color[];
  /** Mana produced per tap (0 for non-sources) */
  produced: number;
}

export interface KeepRules {
  /** Minimum effective mana sources (lands + rock equivalents) */
  minSources: number;
  /** Maximum raw lands before the hand counts as flooded */
  maxLands: number;
  /** A hand with no play at or before this turn is flagged slow */
  slowStartTurn: number;
}

export type HandIssueType = 'screw' | 'flood' | 'color-screw' | 'slow-start' | 'no-spells';
export type HandVerdict = 'keep' | 'risky' | 'mulligan';

export interface HandIssue {
  type: HandIssueType;
  severity: 'warning' | 'critical';
  label: string;
}

export interface HandEvaluation {
  landCount: number;
  rockContribution: number;
  effectiveLands: number;
  spellCount: number;
  castableCount: number;
  /** Parallel to the hand array — earliest turn each card is castable, or null */
  castableTurn: (number | null)[];
  /** Earliest turn any spell in hand can be cast, null if none */
  firstPlayTurn: number | null;
  missingColors: Color[];
  verdict: HandVerdict;
  /** Ordered most severe first */
  issues: HandIssue[];
  /** Dominant issue used for batch aggregation, null when verdict === 'keep' */
  primaryIssue: HandIssueType | null;
}

export interface CastEvent {
  card: SimCard;
  /** The face actually cast — differs from `card.name` for adventures and split cards */
  mode: SimCastMode;
}

export interface TurnAction {
  turn: number;
  /** Null on turn 1 when on the play */
  drawn: SimCard | null;
  landPlayed: SimCard | null;
  cast: CastEvent[];
  /** Mana from permanents that were already in play after the land drop */
  manaAvailable: number;
  /** Mana left on untapped sources at end of turn (rocks cast this turn included) */
  manaUnspent: number;
  landsInPlay: number;
  /** No land was playable and nothing was cast */
  stalled: boolean;
}

export interface TurnSimulation {
  actions: TurnAction[];
  /** First turn a spell was cast, null when the deck never got going */
  firstPlayTurn: number | null;
  /** Start of the unbroken run of stalled turns the simulation ended on */
  stalledFrom: number | null;
  handRemaining: SimCard[];
  /** The draw pile ran out before the turn limit */
  decked: boolean;
}

export interface TurnSimOptions {
  turns: number;
  onThePlay: boolean;
}

/** Turns simulated per hand inside `runBatch`. */
export const BATCH_SIM_TURNS = 5;

export interface BatchResults {
  totalHands: number;
  landDistribution: number[];
  effectiveDistribution: number[];
  /** Hands with verdict 'keep' (strict) — the remainder is split across failureCounts */
  keepableHands: number;
  averageLands: number;
  averageEffective: number;
  failureCounts: Record<HandIssueType, number>;
  averageFirstPlayTurn: number | null;
  /** Index 0 = no play within the window; 1..BATCH_SIM_TURNS = turn of first cast */
  firstPlayTurnDistribution: number[];
  /** Hands that ended the window stalled (no land drop, no cast) */
  stalledByTurn5: number;
}

// ============================================================================
// Library construction
// ============================================================================

const GENERIC_TOKEN_REGEX = /\{(\d+)\}/g;
const MANA_SYMBOL_REGEX = /\{([^}]+)\}/g;
const HYBRID_GENERIC_REGEX = /^(\d+)\//;

/** Layouts whose faces are each castable from hand. Transform backs are not. */
const MULTI_MODE_LAYOUTS: ReadonlySet<CardLayout> = new Set<CardLayout>(['adventure', 'split', 'modal_dfc']);

function emptyPips(): Record<Color, number> {
  return { W: 0, U: 0, B: 0, R: 0, G: 0 };
}

/**
 * Generic mana portion of a cost. Anything the pip counter can't attribute to a
 * colored pip (hybrid, phyrexian, {C}) falls back to generic.
 */
function parseGeneric(manaCost: string | null, cmc: number, colored: number, colorless: number): number {
  if (!manaCost) return cmc;

  let numeric = 0;
  for (const match of manaCost.matchAll(GENERIC_TOKEN_REGEX)) {
    numeric += Number(match[1]);
  }

  const unaccounted = Math.max(0, cmc - numeric - colored - colorless);
  return numeric + colorless + unaccounted;
}

/** Mana value of a cost string. Scryfall omits per-face cmc on most layouts. */
function manaValueOf(manaCost: string): number {
  let value = 0;
  for (const [, symbol] of manaCost.matchAll(MANA_SYMBOL_REGEX)) {
    const numeric = Number(symbol);
    if (Number.isFinite(numeric)) {
      value += numeric;
      continue;
    }
    const hybrid = HYBRID_GENERIC_REGEX.exec(symbol);
    value += hybrid ? Number(hybrid[1]) : symbol === 'X' || symbol === 'Y' || symbol === 'Z' ? 0 : 1;
  }
  return value;
}

function toMode(name: string, manaCost: string | null, cmc: number): SimCastMode {
  const rawPips = countManaPips(manaCost);
  const pips = emptyPips();
  let coloredTotal = 0;
  for (const color of MANA_COLORS) {
    pips[color] = rawPips[color];
    coloredTotal += rawPips[color];
  }

  return { name, cmc, pips, generic: parseGeneric(manaCost, cmc, coloredTotal, rawPips.C) };
}

function isCastableFace(face: CardFace): boolean {
  if (!face.mana_cost) return false;
  // A modal DFC land back is played, not cast, and the land-drop step doesn't model it.
  if (/\bland\b/i.test(face.type_line)) return false;
  // Aftermath halves are only castable from the graveyard.
  return !/\baftermath\b/i.test(face.oracle_text ?? '');
}

/**
 * Castable faces in printed order. Falls back to the card-level cost, which is
 * also the only correct answer for single-faced cards and transform backs.
 */
function buildModes(card: ScryfallCard): SimCastMode[] {
  if (card.card_faces && MULTI_MODE_LAYOUTS.has(card.layout)) {
    const modes = card.card_faces
      .filter(isCastableFace)
      .map((face) => toMode(face.name, face.mana_cost, manaValueOf(face.mana_cost ?? '')));
    if (modes.length > 0) return modes;
  }

  return [toMode(card.name, getManaCost(card), getCmc(card) ?? 0)];
}

function toSimCard(deckCard: EnrichedDeckCard, copyIndex: number): SimCard {
  const card = deckCard.card;
  const isLand = deckCard.classification.is_land;
  const isManaRock = deckCard.classification.is_mana_rock && !isLand;
  const modes = buildModes(card);

  let producedColors: Color[] = [];
  let produced = 0;
  if (isLand) {
    producedColors = deckCard.land_colors.length > 0 ? deckCard.land_colors : deckCard.mana_source_colors;
    produced = Math.max(1, getManaProducedCount(getFullOracleText(card)));
  } else if (isManaRock) {
    producedColors = deckCard.mana_source_colors;
    produced = getManaProducedCount(getFullOracleText(card));
  }

  return {
    key: `${deckCard.id}#${copyIndex}`,
    deckCardId: deckCard.id,
    scryfallId: deckCard.scryfall_id,
    name: card.name,
    imageUris: deckCard.display_image_uris,
    isLand,
    isManaRock,
    cmc: modes[0].cmc,
    modes,
    producedColors,
    produced,
  };
}

/** Expand a single zone by quantity into precomputed sim records. */
export function buildZone(cards: EnrichedDeckCard[], zone: DeckZone): SimCard[] {
  const out: SimCard[] = [];
  for (const deckCard of cards) {
    if (deckCard.zone !== zone) continue;
    for (let i = 0; i < deckCard.quantity; i++) {
      out.push(toSimCard(deckCard, i));
    }
  }
  return out;
}

/**
 * The simulated library: main deck only. Commanders and companions start in the
 * command zone and must never be drawable.
 */
export function buildLibrary(cards: EnrichedDeckCard[]): SimCard[] {
  return buildZone(cards, 'main');
}

// ============================================================================
// Drawing
// ============================================================================

/** Partial Fisher-Yates: only the drawn positions are touched. Mutates `pool`. */
function drawFromPool(pool: SimCard[], size: number): SimCard[] {
  const n = pool.length;
  const count = Math.min(size, n);
  const hand: SimCard[] = new Array(count);

  for (let i = 0; i < count; i++) {
    const j = i + Math.floor(Math.random() * (n - i));
    const swap = pool[i];
    pool[i] = pool[j];
    pool[j] = swap;
    hand[i] = pool[i];
  }

  return hand;
}

export function drawHand(library: SimCard[], size: number): SimCard[] {
  return drawFromPool(library.slice(), size);
}

/** The library minus the cards already in `hand`, in a random draw order. */
export function drawPileFor(library: SimCard[], hand: SimCard[]): SimCard[] {
  const handKeys = new Set(hand.map((card) => card.key));
  const pool = library.filter((card) => !handKeys.has(card.key));
  return drawFromPool(pool, pool.length);
}

/** Lands first, then spells by ascending cost. Stable for equal keys. */
export function sortHand(hand: SimCard[]): SimCard[] {
  return [...hand].sort((a, b) => {
    if (a.isLand !== b.isLand) return a.isLand ? -1 : 1;
    if (a.cmc !== b.cmc) return a.cmc - b.cmc;
    return a.name.localeCompare(b.name);
  });
}

// ============================================================================
// Castability
// ============================================================================

export function requiredColors(mode: SimCastMode): Color[] {
  return MANA_COLORS.filter((color) => mode.pips[color] > 0);
}

/** Every color any face of this card asks for. */
export function cardColors(card: SimCard): Color[] {
  return MANA_COLORS.filter((color) => card.modes.some((mode) => mode.pips[color] > 0));
}

/** The face a player would reach for first when short on mana. */
export function cheapestMode(card: SimCard): SimCastMode {
  return card.modes.reduce((cheapest, mode) => (mode.cmc < cheapest.cmc ? mode : cheapest));
}

/**
 * Kuhn's algorithm: assign every colored pip to a distinct source that makes that
 * color. Returns source index → pip index (-1 when unused), or null when no full
 * matching exists.
 */
function matchPips(pipList: Color[], sources: SimCard[]): number[] | null {
  const assignedTo: number[] = new Array(sources.length).fill(-1);

  const tryAssign = (pipIndex: number, visited: boolean[]): boolean => {
    for (let s = 0; s < sources.length; s++) {
      if (visited[s]) continue;
      if (!sources[s].producedColors.includes(pipList[pipIndex])) continue;
      visited[s] = true;
      if (assignedTo[s] === -1 || tryAssign(assignedTo[s], visited)) {
        assignedTo[s] = pipIndex;
        return true;
      }
    }
    return false;
  };

  for (let p = 0; p < pipList.length; p++) {
    if (!tryAssign(p, new Array<boolean>(sources.length).fill(false))) return null;
  }
  return assignedTo;
}

function pipList(mode: SimCastMode): Color[] {
  const pips: Color[] = [];
  for (const color of MANA_COLORS) {
    for (let i = 0; i < mode.pips[color]; i++) pips.push(color);
  }
  return pips;
}

function totalMana(sources: SimCard[]): number {
  let total = 0;
  for (const source of sources) total += source.produced;
  return total;
}

function canCastMode(mode: SimCastMode, sources: SimCard[]): boolean {
  const pips = pipList(mode);
  if (totalMana(sources) < pips.length + mode.generic) return false;
  if (pips.length === 0) return true;
  return matchPips(pips, sources) !== null;
}

/**
 * Can any face of `card` be paid for by `sources`? Colored pips need distinct
 * matching sources; the total cost is then checked against total mana output.
 */
export function canCast(card: SimCard, sources: SimCard[]): boolean {
  return card.modes.some((mode) => canCastMode(mode, sources));
}

export interface CastPayment {
  mode: SimCastMode;
  /** Sources left untapped after paying */
  remaining: SimCard[];
}

/**
 * Tap `sources` to pay for `mode`, returning the sources left untapped, or null
 * when the cost can't be met. Colored pips claim distinct sources; the generic
 * remainder is filled from the biggest untapped sources so the fewest are used.
 */
function payMode(mode: SimCastMode, sources: SimCard[]): SimCard[] | null {
  const pips = pipList(mode);
  const cost = pips.length + mode.generic;
  if (totalMana(sources) < cost) return null;

  const tapped = new Set<number>();
  if (pips.length > 0) {
    const assignment = matchPips(pips, sources);
    if (!assignment) return null;
    assignment.forEach((pipIndex, sourceIndex) => {
      if (pipIndex !== -1) tapped.add(sourceIndex);
    });
  }

  let mana = 0;
  for (const index of tapped) mana += sources[index].produced;

  if (mana < cost) {
    const spare = sources
      .map((_, index) => index)
      .filter((index) => !tapped.has(index))
      .sort((a, b) => sources[b].produced - sources[a].produced);

    for (const index of spare) {
      if (mana >= cost) break;
      tapped.add(index);
      mana += sources[index].produced;
    }
  }

  if (mana < cost) return null;
  return sources.filter((_, index) => !tapped.has(index));
}

/**
 * Pay for the most expensive face of `card` that `sources` can afford. Casting
 * the big half when it fits and the cheap half when it doesn't is what a player
 * would do, and it is what makes adventures count as early plays.
 */
export function payFor(card: SimCard, sources: SimCard[]): CastPayment | null {
  let best: CastPayment | null = null;

  for (const mode of card.modes) {
    if (best && mode.cmc <= best.mode.cmc) continue;
    const remaining = payMode(mode, sources);
    if (remaining) best = { mode, remaining };
  }

  return best;
}

/** Pick `count` lands, preferring ones that produce the colors the card needs. */
function pickLands(lands: SimCard[], count: number, required: Color[]): SimCard[] {
  if (count >= lands.length) return lands;

  const scored = lands.map((land) => ({
    land,
    score: required.filter((color) => land.producedColors.includes(color)).length * 10 + land.produced,
  }));
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, count).map((entry) => entry.land);
}

/**
 * Mana sources available on `turn`, assuming one land drop per turn from the hand.
 * A rock cast on the current turn only contributes its net mana gain.
 */
function availableSources(hand: SimCard[], turn: number, required: Color[]): SimCard[] {
  const lands = hand.filter((card) => card.isLand);
  const chosen = pickLands(lands, Math.min(turn, lands.length), required);

  const sources = [...chosen];
  for (const rock of hand) {
    if (!rock.isManaRock || rock.produced <= 0) continue;

    const mode = cheapestMode(rock);
    const castTurn = Math.max(mode.cmc, 1);
    if (castTurn > turn || mode.cmc > lands.length) continue;
    if (!canCast(rock, pickLands(lands, Math.min(castTurn, lands.length), requiredColors(mode)))) continue;

    if (castTurn < turn) {
      sources.push(rock);
      continue;
    }
    const net = rock.produced - mode.cmc;
    if (net > 0) sources.push({ ...rock, produced: net });
  }

  return sources;
}

/**
 * Earliest turn `card` can be cast off `hand` alone (no draws), taking the
 * cheapest castable face. Lands return 1 because they can always be played
 * immediately. Null when the hand can never pay for any face.
 */
export function earliestCastTurn(card: SimCard, hand: SimCard[]): number | null {
  if (card.isLand) return 1;

  const others = hand.filter((other) => other !== card);
  const required = cardColors(card);

  for (let turn = 1; turn <= MAX_SIM_TURN; turn++) {
    if (canCast(card, availableSources(others, turn, required))) return turn;
  }
  return null;
}

// ============================================================================
// Evaluation
// ============================================================================

const ISSUE_PRIORITY: HandIssueType[] = ['screw', 'flood', 'no-spells', 'color-screw', 'slow-start'];

function formatColors(colors: Color[]): string {
  return colors.map((color) => `{${color}}`).join('');
}

export function evaluateHand(hand: SimCard[], rules: KeepRules): HandEvaluation {
  const landCount = hand.filter((card) => card.isLand).length;

  let rockContribution = 0;
  for (const card of hand) {
    if (card.isManaRock) {
      rockContribution += rockLandEquivalent(cheapestMode(card).cmc, card.produced, landCount);
    }
  }
  const effectiveLands = landCount + rockContribution;

  const castableTurn = hand.map((card) => earliestCastTurn(card, hand));

  let spellCount = 0;
  let castableCount = 0;
  let firstPlayTurn: number | null = null;
  const neededColors = new Set<Color>();

  hand.forEach((card, index) => {
    if (card.isLand) return;
    spellCount++;
    for (const color of cardColors(card)) neededColors.add(color);

    const turn = castableTurn[index];
    if (turn === null) return;
    castableCount++;
    if (firstPlayTurn === null || turn < firstPlayTurn) firstPlayTurn = turn;
  });

  const availableColors = new Set<Color>();
  for (const card of hand) {
    for (const color of card.producedColors) availableColors.add(color);
  }
  const missingColors = [...neededColors].filter((color) => !availableColors.has(color));

  // Color screw means the hand can't *ever* make a spell's colors. A spell that is
  // merely too expensive for the lands in hand is normal — you draw more lands —
  // so it is left to the slow-start check instead. A card is only stranded when
  // every one of its faces is stranded.
  const hasSources = hand.some((card) => card.isLand || (card.isManaRock && card.produced > 0));
  const colorStranded = hasSources
    ? hand.filter((card) => !card.isLand && card.modes.every((mode) => requiredColors(mode).some((color) => !availableColors.has(color))))
        .length
    : 0;

  const issues: HandIssue[] = [];

  if (effectiveLands < rules.minSources) {
    issues.push({
      type: 'screw',
      severity: 'critical',
      label: `${landCount} land${landCount === 1 ? '' : 's'} — needs ${rules.minSources} sources`,
    });
  }
  if (landCount > rules.maxLands) {
    issues.push({ type: 'flood', severity: 'critical', label: `${landCount} lands — flooded` });
  }
  if (spellCount === 0) {
    issues.push({ type: 'no-spells', severity: 'critical', label: 'all lands — no spells' });
  } else if (colorStranded === spellCount) {
    issues.push({
      type: 'color-screw',
      severity: 'critical',
      label: `no spell matches your colors — missing ${formatColors(missingColors)}`,
    });
  } else if (colorStranded > 0) {
    issues.push({
      type: 'color-screw',
      severity: 'warning',
      label: `missing ${formatColors(missingColors)} for ${colorStranded} spell${colorStranded === 1 ? '' : 's'}`,
    });
  }
  if (spellCount > 0 && (firstPlayTurn === null || firstPlayTurn > rules.slowStartTurn)) {
    issues.push({
      type: 'slow-start',
      severity: 'warning',
      label: firstPlayTurn === null ? 'no play from this hand' : `first play T${firstPlayTurn} — wants T${rules.slowStartTurn}`,
    });
  }

  issues.sort((a, b) => ISSUE_PRIORITY.indexOf(a.type) - ISSUE_PRIORITY.indexOf(b.type));
  issues.sort((a, b) => (a.severity === b.severity ? 0 : a.severity === 'critical' ? -1 : 1));

  const hasCritical = issues.some((issue) => issue.severity === 'critical');
  const verdict: HandVerdict = hasCritical ? 'mulligan' : issues.length > 0 ? 'risky' : 'keep';

  return {
    landCount,
    rockContribution,
    effectiveLands,
    spellCount,
    castableCount,
    castableTurn,
    firstPlayTurn,
    missingColors,
    verdict,
    issues,
    primaryIssue: issues.length > 0 ? issues[0].type : null,
  };
}

// ============================================================================
// Turn simulation
// ============================================================================

/** A trailing run of stalled turns shorter than this is noise, not a stall. */
const STALL_RUN_MIN = 2;

/**
 * Which land to drop this turn. Prefers a land that unlocks a colour the cheapest
 * uncast face needs, then one covering the most still-missing needed colours,
 * then raw output. Falls back to any land.
 */
function chooseLand(hand: SimCard[], inPlay: SimCard[]): SimCard | null {
  const lands = hand.filter((card) => card.isLand);
  if (lands.length === 0) return null;

  const spells = hand.filter((card) => !card.isLand);
  const cheapestSpell = spells.reduce<SimCard | null>(
    (cheapest, spell) => (cheapest === null || cheapestMode(spell).cmc < cheapestMode(cheapest).cmc ? spell : cheapest),
    null,
  );
  const cheapestColors = cheapestSpell ? requiredColors(cheapestMode(cheapestSpell)) : [];

  const needed = new Set<Color>();
  for (const spell of spells) {
    for (const color of cardColors(spell)) needed.add(color);
  }
  const available = new Set<Color>();
  for (const source of inPlay) {
    for (const color of source.producedColors) available.add(color);
  }

  let best = lands[0];
  let bestScore = -Infinity;

  for (const land of lands) {
    const unlocksCheapest = cheapestColors.some((color) => land.producedColors.includes(color) && !available.has(color));
    const helpsCheapest = cheapestColors.some((color) => land.producedColors.includes(color));
    const newNeeded = land.producedColors.filter((color) => needed.has(color) && !available.has(color)).length;
    const anyNeeded = land.producedColors.filter((color) => needed.has(color)).length;

    const score = (unlocksCheapest ? 100 : helpsCheapest ? 20 : 0) + newNeeded * 10 + anyNeeded * 2 + land.produced;
    if (score > bestScore) {
      bestScore = score;
      best = land;
    }
  }

  return best;
}

function removeCard(hand: SimCard[], card: SimCard): void {
  const index = hand.indexOf(card);
  if (index !== -1) hand.splice(index, 1);
}

/**
 * Solitaire turn model: draw one card, play the best available land, then cast an
 * affordable commander before greedily casting the highest-CMC spells until nothing
 * else fits. Sequencing is deliberately greedy — skipping an expensive spell so two
 * cheap ones fit is not attempted.
 *
 * `drawPile` must already exclude the cards in `hand` (see `drawPileFor`).
 */
export function simulateTurns(hand: SimCard[], drawPile: SimCard[], options: TurnSimOptions, commanders: SimCard[] = []): TurnSimulation {
  const remaining = [...hand];
  const commandZone = [...commanders];
  const inPlay: SimCard[] = [];
  const actions: TurnAction[] = [];

  let drawIndex = 0;
  let decked = false;
  let firstPlayTurn: number | null = null;

  for (let turn = 1; turn <= options.turns; turn++) {
    let drawn: SimCard | null = null;
    if (turn > 1 || !options.onThePlay) {
      if (drawIndex >= drawPile.length) {
        decked = true;
        break;
      }
      drawn = drawPile[drawIndex++];
      remaining.push(drawn);
    }

    const landPlayed = chooseLand(remaining, inPlay);
    if (landPlayed) {
      removeCard(remaining, landPlayed);
      inPlay.push(landPlayed);
    }

    let sources = [...inPlay];
    const manaAvailable = totalMana(sources);

    const cast: CastEvent[] = [];
    for (;;) {
      // Commanders take priority. Other spells follow the greedy highest-CMC heuristic.
      let best: { card: SimCard; payment: CastPayment } | null = null;
      for (const commander of commandZone) {
        const payment = payFor(commander, sources);
        if (payment) {
          best = { card: commander, payment };
          break;
        }
      }
      if (!best) {
        for (const card of remaining) {
          if (card.isLand) continue;
          const payment = payFor(card, sources);
          if (payment && (!best || payment.mode.cmc > best.payment.mode.cmc)) best = { card, payment };
        }
      }
      if (!best) break;

      sources = best.payment.remaining;
      if (commandZone.includes(best.card)) removeCard(commandZone, best.card);
      else removeCard(remaining, best.card);
      cast.push({ card: best.card, mode: best.payment.mode });

      // A rock resolves untapped, so it can help pay for later spells this turn.
      if (best.card.isManaRock && best.card.produced > 0) {
        inPlay.push(best.card);
        sources.push(best.card);
      }
    }

    if (cast.length > 0 && firstPlayTurn === null) firstPlayTurn = turn;

    actions.push({
      turn,
      drawn,
      landPlayed,
      cast,
      manaAvailable,
      manaUnspent: totalMana(sources),
      landsInPlay: inPlay.filter((card) => card.isLand).length,
      stalled: !landPlayed && cast.length === 0,
    });
  }

  let stallRun = 0;
  for (let i = actions.length - 1; i >= 0 && actions[i].stalled; i--) stallRun++;

  return {
    actions,
    firstPlayTurn,
    stalledFrom: stallRun >= STALL_RUN_MIN ? actions[actions.length - stallRun].turn : null,
    handRemaining: remaining,
    decked,
  };
}

// ============================================================================
// Batch
// ============================================================================

function emptyFailureCounts(): Record<HandIssueType, number> {
  return { screw: 0, flood: 0, 'color-screw': 0, 'slow-start': 0, 'no-spells': 0 };
}

export function runBatch(library: SimCard[], handSize: number, iterations: number, rules: KeepRules, onThePlay = true): BatchResults {
  const landDistribution = new Array<number>(handSize + 1).fill(0);
  const effectiveDistribution = new Array<number>(handSize + 1).fill(0);
  const firstPlayTurnDistribution = new Array<number>(BATCH_SIM_TURNS + 1).fill(0);
  const failureCounts = emptyFailureCounts();

  let keepableHands = 0;
  let totalLands = 0;
  let totalEffective = 0;
  let firstPlaySum = 0;
  let firstPlayHands = 0;
  let stalledByTurn5 = 0;

  const pool = library.slice();

  for (let i = 0; i < iterations; i++) {
    // Deal the opening hand and the turn draws together so every card the hand
    // sees comes from the same uniformly shuffled slice of the pool.
    const dealt = drawFromPool(pool, handSize + BATCH_SIM_TURNS);
    const hand = dealt.slice(0, handSize);
    const evaluation = evaluateHand(hand, rules);

    landDistribution[Math.min(evaluation.landCount, handSize)]++;
    effectiveDistribution[Math.min(Math.round(evaluation.effectiveLands), handSize)]++;
    totalLands += evaluation.landCount;
    totalEffective += evaluation.effectiveLands;

    if (evaluation.primaryIssue === null) keepableHands++;
    else failureCounts[evaluation.primaryIssue]++;

    if (evaluation.firstPlayTurn !== null) {
      firstPlaySum += evaluation.firstPlayTurn;
      firstPlayHands++;
    }

    const turnSim = simulateTurns(hand, dealt.slice(handSize), { turns: BATCH_SIM_TURNS, onThePlay });
    firstPlayTurnDistribution[turnSim.firstPlayTurn ?? 0]++;
    if (turnSim.stalledFrom !== null) stalledByTurn5++;
  }

  return {
    totalHands: iterations,
    landDistribution,
    effectiveDistribution,
    keepableHands,
    averageLands: iterations > 0 ? totalLands / iterations : 0,
    averageEffective: iterations > 0 ? totalEffective / iterations : 0,
    failureCounts,
    averageFirstPlayTurn: firstPlayHands > 0 ? firstPlaySum / firstPlayHands : null,
    firstPlayTurnDistribution,
    stalledByTurn5,
  };
}

// ============================================================================
// Sampling error
// ============================================================================

/** 95% confidence z-score. */
const Z_95 = 1.96;

/**
 * Half-width of the 95% confidence interval on a run's keep rate, as a proportion.
 * A batch is a random sample, so its keep % is only ever accurate to within this.
 */
export function keepRateMargin(results: BatchResults): number {
  if (results.totalHands === 0) return 0;
  const rate = results.keepableHands / results.totalHands;
  return Z_95 * Math.sqrt((rate * (1 - rate)) / results.totalHands);
}

/**
 * True when the keep-rate change between two runs is larger than the sampling
 * noise of both runs combined — i.e. the deck really did get better or worse.
 */
export function isKeepRateChangeSignificant(previous: BatchResults, current: BatchResults): boolean {
  const change = Math.abs(current.keepableHands / current.totalHands - previous.keepableHands / previous.totalHands);
  const combined = Math.hypot(keepRateMargin(previous), keepRateMargin(current));
  return change > combined;
}
